// Bounded, time-limited FIFO behind the kill feed. Pure: the caller supplies the clock.

export const KILLFEED_MAX = 6;
export const KILLFEED_LIFETIME_MS = 5000;

export class KillfeedQueue<T> {
  private values: T[] = [];
  private expires: number[] = [];

  constructor(
    readonly maxEntries = KILLFEED_MAX,
    readonly lifetimeMs = KILLFEED_LIFETIME_MS,
  ) {}

  get length(): number {
    return this.values.length;
  }

  /** Append `value` (oldest first). Returns the oldest entry when the limit pushed it out, else undefined. */
  push(value: T, nowMs: number): T | undefined {
    this.values.push(value);
    this.expires.push(nowMs + this.lifetimeMs);
    if (this.values.length > this.maxEntries) {
      this.expires.shift();
      return this.values.shift();
    }
    return undefined;
  }

  /** Time the oldest entry expires at (Infinity when empty): a cheap per-frame check. */
  nextExpiry(): number {
    return this.expires.length > 0 ? this.expires[0]! : Infinity;
  }

  /** Remove and return the oldest entry if it expired by `nowMs`. Call in a loop. */
  popExpired(nowMs: number): T | undefined {
    if (this.expires.length === 0 || this.expires[0]! > nowMs) return undefined;
    this.expires.shift();
    return this.values.shift();
  }

  /** Remove everything, oldest first. */
  drain(): T[] {
    const all = this.values;
    this.values = [];
    this.expires = [];
    return all;
  }
}
